import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatINR, payViaGateway } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { DeviceMobile, CreditCard, Bank, Wallet, ShieldCheck, ArrowsClockwise, Lock, CheckCircle, SealCheck } from "@phosphor-icons/react";

const METHODS = [
  { id: "upi", icon: DeviceMobile, label: "UPI", sub: "Pay using any UPI app" },
  { id: "card", icon: CreditCard, label: "Credit / Debit Card", sub: "Visa, Mastercard, Rupay" },
  { id: "netbanking", icon: Bank, label: "Net Banking", sub: "All major banks" },
  { id: "wallet", icon: Wallet, label: "Wallets", sub: "Paytm, PhonePe & more" },
];

const WHY = [
  { icon: ArrowsClockwise, t: "Refundable", d: "Fully refunded if due diligence does not clear." },
  { icon: Lock, t: "Secure Your Slot", d: "Your interest is recorded and your share reserved." },
  { icon: ShieldCheck, t: "Serious Commitment", d: "Helps us serve genuine partners better." },
];

export default function Payment() {
  const { intentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [intent, setIntent] = useState(null);
  const [method, setMethod] = useState("upi");
  const [amount, setAmount] = useState(5000);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/intents/me").then(({ data }) => {
      const it = data.find((i) => i.id === intentId);
      if (!it) { toast.error("Application not found"); navigate("/dashboard"); return; }
      setIntent(it); setAmount(it.intent_fee || 5000);
      if (it.payment_status === "paid") setDone(true);
    });
  }, [intentId, navigate]);

  const pay = async () => {
    setLoading(true);
    try {
      const res = await payViaGateway({ purpose: "intent_fee", amount, ref_id: intentId, user });
      if (res.status === "paid") { setDone(true); toast.success("Payment successful"); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setLoading(false); }
  };

  if (!intent) return <div className="px-6 py-32 text-center text-ebg-muted font-data">Loading…</div>;

  if (done) {
    return (
      <div data-testid="payment-success" className="px-6 py-24 max-w-xl mx-auto text-center">
        <CheckCircle size={72} weight="fill" className="text-gold mx-auto" />
        <h1 className="font-display text-4xl text-white mt-6">Slot locked.</h1>
        <p className="text-ebg-muted mt-3">Your intent fee for <span className="text-white">{intent.brand_name}</span> is received and your profile has entered due diligence.</p>
        <button onClick={() => navigate("/dashboard")} className="mt-8 btn-gold px-7 py-3 rounded-md">Go to Dashboard</button>
      </div>
    );
  }

  return (
    <div data-testid="payment-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <p className="eyebrow mb-2">Pay Intent Fee</p>
      <h1 className="font-display text-4xl md:text-5xl text-white">Secure your slot.</h1>
      <p className="text-ebg-muted mt-2">A refundable intent fee for <span className="text-gold">{intent.brand_name}</span>.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-8 shadow-2xl">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6">
            <p className="text-xs text-amber-700">DEMO MODE — Razorpay sandbox stub (no real charge) until live keys are added.</p>
          </div>

          <h2 className="font-display text-2xl text-navy-950 mb-1">Intent Fee Details</h2>
          <p className="text-gold font-semibold text-2xl mt-1">₹5,000 – ₹10,000 <span className="text-xs text-gray-400 font-normal">(Refundable)</span></p>
          <label className="text-xs font-medium text-gray-600 mt-5 block mb-1.5">Enter Amount</label>
          <input type="number" min={5000} max={10000} value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="payment-amount"
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-navy-950 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none" />
          <div className="flex gap-3 mt-3">
            {[5000, 7500, 10000].map((v) => (
              <button key={v} type="button" onClick={() => setAmount(v)} data-testid={`payment-preset-${v}`}
                className={`flex-1 py-2 rounded-md border text-sm transition-colors ${amount === v ? "border-gold bg-gold/10 text-navy-950 font-medium" : "border-gray-200 text-gray-500 hover:border-gold"}`}>{formatINR(v)}</button>
            ))}
          </div>

          <h2 className="font-display text-2xl text-navy-950 mt-8 mb-4">Select Payment Method</h2>
          <div className="space-y-3">
            {METHODS.map((m) => (
              <button key={m.id} type="button" onClick={() => setMethod(m.id)} data-testid={`payment-method-${m.id}`}
                className={`w-full flex items-center gap-4 rounded-lg border p-4 text-left transition-colors ${method === m.id ? "border-gold bg-gold/5" : "border-gray-200 hover:border-gray-300"}`}>
                <m.icon size={24} className={method === m.id ? "text-gold" : "text-gray-400"} />
                <div className="flex-1"><p className="text-sm font-medium text-navy-950">{m.label}</p><p className="text-xs text-gray-400">{m.sub}</p></div>
                <span className={`w-4 h-4 rounded-full border-2 ${method === m.id ? "border-gold bg-gold" : "border-gray-300"}`} />
              </button>
            ))}
          </div>

          <button onClick={pay} disabled={loading} data-testid="payment-pay-btn" className="w-full mt-7 btn-gold py-3.5 rounded-lg disabled:opacity-50">
            {loading ? "Processing…" : `Pay ${formatINR(amount)}`}
          </button>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
            {[[Lock, "100% Secure", "SSL Encrypted"], [SealCheck, "PCI DSS", "Compliant"], [ShieldCheck, "Trusted by", "Thousands of investors"]].map(([Icon, a, b]) => (
              <div key={a} className="flex items-center gap-2"><Icon size={20} className="text-gold" /><div><p className="text-xs text-navy-950 font-medium leading-none">{a}</p><p className="text-[0.65rem] text-gray-400 mt-0.5">{b}</p></div></div>
            ))}
          </div>
        </div>

        {/* Why intent fee */}
        <aside className="lg:col-span-1">
          <div className="bg-navy-900 border border-white/10 rounded-2xl p-6 lg:sticky lg:top-24">
            <h3 className="font-display text-2xl text-white mb-5">Why Intent Fee?</h3>
            <div className="space-y-5">
              {WHY.map((w) => (
                <div key={w.t} className="flex gap-3">
                  <div className="grid place-items-center w-10 h-10 rounded-lg bg-gold/10 border border-gold/20 flex-shrink-0"><w.icon size={20} className="text-gold" weight="light" /></div>
                  <div><p className="text-sm text-white font-medium">{w.t}</p><p className="text-xs text-ebg-muted mt-1 leading-relaxed">{w.d}</p></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-ebg-muted mt-6 leading-relaxed border-t border-white/10 pt-4">This amount is refundable if due diligence does not clear, and is adjusted against your final investment.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
